<template>
  <div class="compact-members-page">
    <v-container fluid class="members-container">
      <!-- Compact Header with Year Filter on Right -->
      <div class="page-header">
        <div class="header-content">
          <div class="title-section">
            <h1 class="page-title">
              <v-icon color="#667eea" size="24">mdi-shield-star</v-icon>
              Board & Committee Members
            </h1>
          </div>
          <div class="filters-section">
            <span class="year-label">Year:</span>
            <v-select
              v-model="selectedYear"
              :items="availableYears"
              outlined
              dense
              hide-details
              @change="loadCommitteeData"
              class="year-filter"
              style="width: 120px;"
            ></v-select>
          </div>
        </div>
      </div>

      <!-- Compact Stats Bar -->
      <div class="stats-bar">
        <div class="stat-item">
          <v-icon color="#667eea" size="16">mdi-shield-account</v-icon>
          <span class="stat-value">{{ boardMembers.length }}</span>
          <span class="stat-label">Board</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <v-icon color="#764ba2" size="16">mdi-account-star</v-icon>
          <span class="stat-value">{{ executiveMembers.length }}</span>
          <span class="stat-label">Executive</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <v-icon color="#8ebf42" size="16">mdi-account-multiple</v-icon>
          <span class="stat-value">{{ subCommitteeMembers.length }}</span>
          <span class="stat-label">Sub Committee</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <v-icon color="#f093fb" size="16">mdi-account-group</v-icon>
          <span class="stat-value">{{ data.length }}</span>
          <span class="stat-label">Total</span>
        </div>
      </div>

      <!-- Members Groups -->
      <div class="members-groups">
        <!-- Board of Directors -->
        <div v-if="filteredBoardMembers.length > 0" class="member-group">
          <div class="group-header">
            <v-icon color="#667eea" size="18">mdi-shield-account</v-icon>
            <h2 class="group-title">Board of Directors</h2>
            <span class="group-count">({{ filteredBoardMembers.length }})</span>
          </div>
          <v-row dense class="members-row">
            <v-col
              v-for="(member, index) in filteredBoardMembers"
              :key="`board-${index}`"
              cols="12"
              sm="6"
              md="4"
              lg="3"
            >
              <div class="member-card">
                <div class="member-avatar board">
                  <v-icon color="white" size="20">mdi-account</v-icon>
                </div>
                <div class="member-info">
                  <h3 class="member-name">{{ member.name }}</h3>
                  <p class="member-role">{{ member.responsibility }}</p>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Executive Committee -->
        <div v-if="filteredExecutiveMembers.length > 0" class="member-group">
          <div class="group-header">
            <v-icon color="#764ba2" size="18">mdi-account-star</v-icon>
            <h2 class="group-title">Executive Committee</h2>
            <span class="group-count">({{ filteredExecutiveMembers.length }})</span>
          </div>
          <v-row dense class="members-row">
            <v-col
              v-for="(member, index) in filteredExecutiveMembers"
              :key="`exec-${index}`"
              cols="12"
              sm="6"
              md="4"
              lg="3"
            >
              <div class="member-card">
                <div class="member-avatar executive">
                  <v-icon color="white" size="20">mdi-account</v-icon>
                </div>
                <div class="member-info">
                  <h3 class="member-name">{{ member.name }}</h3>
                  <p class="member-role">{{ member.responsibility }}</p>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Sub Committees -->
        <div v-if="filteredSubCommitteeMembers.length > 0" class="member-group">
          <div class="group-header">
            <v-icon color="#8ebf42" size="18">mdi-account-multiple</v-icon>
            <h2 class="group-title">Sub Committees</h2>
            <span class="group-count">({{ filteredSubCommitteeMembers.length }})</span>
          </div>
          <v-row dense class="members-row">
            <v-col
              v-for="(member, index) in filteredSubCommitteeMembers"
              :key="`sub-${index}`"
              cols="12"
              sm="6"
              md="4"
              lg="3"
            >
              <div class="member-card">
                <div class="member-avatar sub">
                  <v-icon color="white" size="20">mdi-account</v-icon>
                </div>
                <div class="member-info">
                  <h3 class="member-name">{{ member.name }}</h3>
                  <p class="member-role">{{ member.responsibility }}</p>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import ReportServices from '@/services/ReportServices'

export default {
  name: "App",
  data() {
    return {
      title: "Cltts Sangam Board and Committee Members",
      search: "",
      selectedYear: "2026",
      availableYears: ["2026", "2025", "2024", "2023", "2022"],
      loading: false,
      data: []
    }
  },
  computed: {
    boardMembers() {
      return this.data.filter(m => m.group === 'BOARD OF DIRECTOR')
    },
    executiveMembers() {
      return this.data.filter(m => m.group === 'EXECUTIVE COMMITTEE')
    },
    subCommitteeMembers() {
      return this.data.filter(m => m.group === 'SUB COMMITTEE')
    },
    filteredBoardMembers() {
      return this.filterMembers(this.boardMembers)
    },
    filteredExecutiveMembers() {
      return this.filterMembers(this.executiveMembers)
    },
    filteredSubCommitteeMembers() {
      return this.filterMembers(this.subCommitteeMembers)
    }
  },
  methods: {
    filterMembers(members) {
      if (!this.search) return members
      const searchLower = this.search.toLowerCase()
      return members.filter(m =>
        m.name.toLowerCase().includes(searchLower) ||
        m.responsibility.toLowerCase().includes(searchLower) ||
        m.group.toLowerCase().includes(searchLower)
      )
    },
    async loadCommitteeData() {
      this.loading = true
      try {
        const response = await ReportServices.getCommitteeByYear(this.selectedYear)
        if (response.data && response.data.length > 0) {
          this.data = response.data
        } else {
          // If no data found for selected year, show empty
          this.data = []
        }
      } catch (error) {
        // Keep existing hardcoded data as fallback
        this.$notify({
          title: 'Error',
          message: 'Failed to load committee data. Showing cached data.',
          type: 'warning'
        })
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    // Load data for the default selected year on component mount
    this.loadCommitteeData()
  }
}
</script>


<style scoped lang="scss">
.compact-members-page {
  background: #f5f7fa;
  min-height: 100vh;
  padding: 10px;
}

.members-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
}

/* Compact Page Header */
.page-header {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 6px;
  padding: 8px 16px;
  margin-bottom: 8px;
  box-shadow: 0 1px 4px rgba(16, 185, 129, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.title-section {
  flex: 1;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-title .v-icon {
  color: #ffffff !important;
  font-size: 20px !important;
}

.filters-section {
  display: flex;
  gap: 8px;
  align-items: center;
}

.year-label {
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
}

.year-filter {
  ::v-deep .v-input__slot {
    background: rgba(255, 255, 255, 0.95) !important;
  }

  ::v-deep .v-label {
    color: #64748b !important;
  }

  ::v-deep input {
    color: #1e293b !important;
  }
}

/* Compact Stats Bar */
.stats-bar {
  background: white;
  border-radius: 6px;
  padding: 8px 16px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-around;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  flex-wrap: wrap;
  gap: 12px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-value {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.stat-divider {
  width: 1px;
  height: 20px;
  background: #e2e8f0;
}

/* Members Groups */
.members-groups {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.member-group {
  background: white;
  border-radius: 6px;
  padding: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.group-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e2e8f0;
}

.group-title {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.group-count {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.members-row {
  margin-top: 0;
}

/* Compact Member Cards */
.member-card {
  background: #f8fafc;
  border-radius: 4px;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  height: 100%;

  &:hover {
    background: white;
    border-color: #cbd5e1;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
}

.member-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &.board {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }

  &.executive {
    background: linear-gradient(135deg, #764ba2 0%, #f093fb 100%);
  }

  &.sub {
    background: linear-gradient(135deg, #8ebf42 0%, #72a834 100%);
  }
}

.member-avatar .v-icon {
  font-size: 20px !important;
}

.member-info {
  flex: 1;
  min-width: 0;
}

.member-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 2px 0;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.member-role {
  font-size: 11px;
  color: #64748b;
  margin: 0;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Responsive Design */
@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .filters-section {
    justify-content: flex-start;
  }

  .stats-bar {
    gap: 8px;
    padding: 10px 16px;
  }

  .stat-item {
    font-size: 12px;
  }

  .stat-value {
    font-size: 16px;
  }
}

@media (max-width: 600px) {
  .compact-members-page {
    padding: 12px;
  }

  .page-title {
    font-size: 18px;
  }

  .filters-section {
    gap: 8px;
  }

  .year-label {
    font-size: 14px;
  }

  .year-filter {
    width: 100px !important;
  }

  .stats-bar {
    flex-wrap: wrap;
    gap: 12px;
  }

  .stat-divider {
    display: none;
  }

  .stat-item {
    flex: 1 1 45%;
  }

  .group-title {
    font-size: 15px;
  }

  .member-card {
    padding: 10px;
    gap: 10px;
  }

  .member-avatar {
    width: 40px;
    height: 40px;
  }

  .member-avatar .v-icon {
    font-size: 20px !important;
  }

  .member-name {
    font-size: 13px;
  }

  .member-role {
    font-size: 11px;
  }
}
</style>
